import React, { useMemo, useState, lazy, Suspense, useEffect } from 'react';
import moment from 'moment';
import { Box, Typography, Button, Alert } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import LinearProgress from '@mui/material/LinearProgress';
import { toast } from 'react-toastify';
// Components
import FilterUtil from './components/FilterUtil';
import BorderSection from '../../components/BorderSection';
import BarApexChart from '../../components/BarApexChart';
import BarGroupApexChart from '../../components/BarGroupApexChart';
// import BarStackedApexChart from '../../components/BarStackedApexChart';
import DonutApexChart from '../../components/DonutApexChart';
// Modals
import PresentedvsCoverageModal from './components/modals/PresentedvsCoverageModal';
import ProductTeamPresentedModal from './components/modals/ProductTeamPresentedModal';
import ProductPresentedModal from './components/modals/ProductPresentedModal';
import ProductPresentedPerSpecialty from './components/modals/ProductPresentedPerSpecialty';
import PerGroupModal from './components/modals/PerGroupModal';
import PerSegmentModal from './components/modals/ProductSegmentModal';
// Context
// import AppConst from '../../components/AppLayout/const';
import useProductUtilizationDashboardContext from './context/useProductUtilizationDashboardContext';
// API
import {
  postPresentedvsCoverageChart,
  postProductTeamPresentedChart,
  postProductPresentedChart,
  postProductPresentedPerSpecialtyChart,
  postPerGroupChart,
  postPerSegmentChart,
} from '../../api/productUtilDashboardApi';

const BarStackedApexChart = lazy(() => import('../../components/BarStackedApexChart'));

export default function ProductUtilizationDashboard() {
  const defaultFilterForm = {
    team: { label: 'ALL', value: null },
    agenda: [],
    date_from: moment().startOf('month').format('YYYY-MM-DD'),
    date_to: moment().endOf('month').format('YYYY-MM-DD'),
  };
  // const { onSetTitle } = useContext(AppConst.AppContext);
  const [didGenerate, setDidGenerate] = useState(false);
  const [filterForm, setFilterForm] = useState(defaultFilterForm);
  const [pvcChart, setPvcChart] = useState({
    toggle: false,
    label: [],
    value: [],
    total_percentage: 0,
  });
  const [ptpChart, setPtpChart] = useState({ toggle: false, label: [], value: [], percentage: [] });
  const [ppChart, setPpChart] = useState({ toggle: false, label: [], value: [] });
  const [pppsChart, setPppsChart] = useState({ toggle: false, label: [], value: [] });
  const [pgChart, setPgChart] = useState({ toggle: false, label: [], value: [], value1: [] });
  const [pbsChart, setPbsChart] = useState({ toggle: false, label: [], value: [] });
  const [pbsChartToggle, setPbsChartToggle] = useState(false);
  // Modal State
  const [pvcModal, setPvcModal] = useState(false);
  const [ptpModal, setPtpModal] = useState(false);
  const [ppModal, setPpModal] = useState(false);
  const [pppsModal, setPppsModal] = useState(false);
  const [pgModal, setPgModal] = useState(false);
  const [pbsModal, setPbsModal] = useState(false);

  // onSetTitle('Product Utilization Dashboard');

  const handleViewChartBreakdown = (name) => {
    if (name === 'pvc') {
      setPvcModal(true);
    }

    if (name === 'ptp') {
      setPtpModal(true);
    }

    if (name === 'pp') {
      setPpModal(true);
    }

    if (name === 'ppps') {
      setPppsModal(true);
    }

    if (name === 'pg') {
      setPgModal(true);
    }

    if (name === 'pbs') {
      setPbsModal(true);
    }
  };

  const handleGlobalRequest = (reset = null) => {
    let request;
    if (reset === 'clear') {
      request = {
        team: defaultFilterForm?.team?.value,
        agenda_id:
          defaultFilterForm?.agenda[0].value !== null
            ? defaultFilterForm?.agenda?.map((row) => row.value)
            : [],
        date_from: defaultFilterForm?.date_from,
        date_to: defaultFilterForm?.date_to,
      };
    } else {
      request = {
        team: filterForm?.team?.value,
        agenda_id:
          filterForm?.agenda[0].value !== null ? filterForm?.agenda?.map((row) => row.value) : [],
        date_from: filterForm?.date_from,
        date_to: filterForm?.date_to,
      };
    }

    return request;
  };

  const handlePresentedvsCoverageChart = async (reset = null) => {
    setPvcChart({ ...pvcChart, toggle: false });
    const request = handleGlobalRequest(reset);
    const response = await postPresentedvsCoverageChart(request);
    const { code, data } = response.data;

    if (code === '200') {
      const chartData = {
        ...pvcChart,
        toggle: true,
        label: data.map((row) => row.name),
        value: data.map((row) => row.data),
        percentage: data.map((row) => row.percentage),
        total_percentage: response?.data?.total_percentage ?? 0,
      };
      setPvcChart(chartData);
    }
  };

  const handleProductTeamPresentedChart = async (reset = null) => {
    setPtpChart({ ...ptpChart, toggle: false });
    const request = handleGlobalRequest(reset);
    const response = await postProductTeamPresentedChart(request);
    const { code, data, labels } = response.data;
    if (code === '200') {
      setPtpChart({
        ...ptpChart,
        toggle: true,
        label: labels,
        value: data,
      });
    }
  };

  const handleProductPresentedChart = async (reset = null) => {
    setPpChart({ ...ppChart, toggle: false });
    const request = handleGlobalRequest(reset);
    const response = await postProductPresentedChart(request);
    const { code, data, labels } = response.data;
    if (code === '200') {
      setPpChart({
        ...ppChart,
        toggle: true,
        label: labels,
        value: data,
      });
    }
  };

  const handleProductPresetedPerSpecialtyChart = async (reset = null) => {
    setPppsChart({ ...pppsChart, toggle: false });
    const request = handleGlobalRequest(reset);
    const response = await postProductPresentedPerSpecialtyChart(request);
    const { code, data, labels } = response.data;
    if (code === '200') {
      setPppsChart({
        ...pppsChart,
        toggle: true,
        label: labels,
        value: data,
      });
    }
  };

  const handlePerGroupChart = async (reset = null) => {
    setPgChart({ ...pgChart, toggle: false });
    const request = handleGlobalRequest(reset);
    const response = await postPerGroupChart(request);
    const { code, data } = response.data;

    if (code === '200') {
      const chartData = {
        ...pgChart,
        toggle: true,
        label: data.map((row) => row.md_group),
        value: data.map((row) => row.presented),
        value1: data.map((row) => row.coverage),
      };
      setPgChart(chartData);
    }
  };

  const handleProductbySegmentChart = async (reset = null) => {
    setPbsChartToggle(false);
    setPbsChart({ ...pgChart, toggle: false });
    const request = handleGlobalRequest(reset);
    const response = await postPerSegmentChart(request);
    const { code, data } = response.data;

    if (code === '200') {
      const chartData = {
        ...pbsChart,
        toggle: true,
        label: response.data.label,
        value: data,
      };
      setPbsChart(chartData);
      setTimeout(() => {
        setPbsChartToggle(true);
      }, 3000);
    }
  };

  const handleGenerateReport = () => {
    if (filterForm?.agenda?.length < 1) {
      toast.error('Please select at least one agenda to proceed.');
      return;
    }
    setDidGenerate(true);
    handlePresentedvsCoverageChart();
    handleProductTeamPresentedChart();
    handleProductPresentedChart();
    handleProductPresetedPerSpecialtyChart();
    handlePerGroupChart();
    handleProductbySegmentChart();
  };

  const handleClearReport = () => {
    setFilterForm(defaultFilterForm);
    setDidGenerate(false);
    handlePresentedvsCoverageChart('clear');
    handleProductTeamPresentedChart('clear');
    handleProductPresentedChart('clear');
    handleProductPresetedPerSpecialtyChart('clear');
    handlePerGroupChart('clear');
    handleProductbySegmentChart('clear');
  };

  useEffect(() => {
    toast.dismiss();
  }, []);
  return (
    <useProductUtilizationDashboardContext.Provider
      value={useMemo(
        () => ({
          // GET
          filterForm,
          pvcModal,
          ptpModal,
          ppModal,
          pppsModal,
          pgModal,
          pbsModal,
          // SET
          setFilterForm,
          setPvcModal,
          setPtpModal,
          setPpModal,
          setPppsModal,
          setPgModal,
          setPbsModal,
          // FUNCTION
          handleGenerateReport,
          handleClearReport,
        }),
        [filterForm, pvcModal, ptpModal, ppModal, pppsModal, pgModal, pbsModal],
      )}
    >
      <Box>
        <FilterUtil />
      </Box>

      {didGenerate ? (
        <>
          <Grid container spacing={2} pt={4} mt={5}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <BorderSection>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography
                      variant='button'
                      gutterBottom
                      sx={{ display: 'block', fontWeight: 'bold' }}
                    >
                      Presented vs Coverage
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        variant='text'
                        color='primary'
                        onClick={() => handleViewChartBreakdown('pvc')}
                      >
                        View Breakdown
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {pvcChart.toggle ? (
                  <DonutApexChart name='pvc' data={pvcChart} />
                ) : (
                  <LinearProgress />
                )}
              </BorderSection>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <BorderSection>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography
                      variant='button'
                      gutterBottom
                      sx={{ display: 'block', fontWeight: 'bold' }}
                    >
                      Product Team Presented
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        variant='text'
                        color='primary'
                        onClick={() => handleViewChartBreakdown('ptp')}
                      >
                        View Breakdown
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {ptpChart.toggle ? <BarApexChart name='ptp' data={ptpChart} /> : <LinearProgress />}
              </BorderSection>
            </Grid>
          </Grid>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <BorderSection>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography
                      variant='button'
                      gutterBottom
                      sx={{ display: 'block', fontWeight: 'bold' }}
                    >
                      Product Presented
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        variant='text'
                        color='primary'
                        onClick={() => handleViewChartBreakdown('pp')}
                      >
                        View Breakdown
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {ppChart.toggle ? <BarApexChart name='pp' data={ppChart} /> : <LinearProgress />}
              </BorderSection>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <BorderSection>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography
                      variant='button'
                      gutterBottom
                      sx={{ display: 'block', fontWeight: 'bold' }}
                    >
                      Product Presented Per Specialty
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        variant='text'
                        color='primary'
                        onClick={() => handleViewChartBreakdown('ppps')}
                      >
                        View Breakdown
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {pppsChart.toggle ? (
                  <BarApexChart name='ppps' data={pppsChart} />
                ) : (
                  <LinearProgress />
                )}
              </BorderSection>
            </Grid>
          </Grid>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <BorderSection>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography
                      variant='button'
                      gutterBottom
                      sx={{ display: 'block', fontWeight: 'bold' }}
                    >
                      Per Group
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        variant='text'
                        color='primary'
                        onClick={() => handleViewChartBreakdown('pg')}
                      >
                        View Breakdown
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {pgChart.toggle ? (
                  <BarGroupApexChart name='pg' data={pgChart} />
                ) : (
                  <LinearProgress />
                )}
              </BorderSection>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <BorderSection>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography
                      variant='button'
                      gutterBottom
                      sx={{ display: 'block', fontWeight: 'bold' }}
                    >
                      Product by Segment
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        variant='text'
                        color='primary'
                        onClick={() => handleViewChartBreakdown('pbs')}
                      >
                        View Breakdown
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {pbsChartToggle ? (
                  <Suspense fallback={<LinearProgress />}>
                    <BarStackedApexChart name='pbs' data={pbsChart} />
                  </Suspense>
                ) : (
                  <LinearProgress />
                )}
              </BorderSection>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box mt={5}>
          <Alert variant='outlined' severity='info'>
            Please generate report
          </Alert>
        </Box>
      )}
      <PresentedvsCoverageModal />
      <ProductTeamPresentedModal />
      <ProductPresentedModal />
      <ProductPresentedPerSpecialty />
      <PerGroupModal />
      <PerSegmentModal />
    </useProductUtilizationDashboardContext.Provider>
  );
}
